<script>
import { computed } from 'vue';
import { mapGetters } from 'vuex';
import { useStore } from 'dashboard/composables/store';
import { useAdmin } from 'dashboard/composables/useAdmin';
import {
  getUserPermissions,
  hasPermissions,
} from 'dashboard/helper/permissionsHelper';
import { useAlert } from 'dashboard/composables';
import ContactAPI from 'dashboard/api/contacts';
import CompanyAPI from 'dashboard/api/companies';
import CompanyLoadingIndicator from '../../companies/components/CompanyLoadingIndicator.vue';
import EditRelatedCompaniesModal from './EditRelatedCompaniesModal.vue';
import AddCompanyAssociationModal from '../../companies/components/profile/AddCompanyAssociationModal.vue';
import PanelEmptyState from '../../conversation/sidebar/PanelEmptyState.vue';

export default {
  name: 'ContactRelatedCompanies',
  components: {
    CompanyLoadingIndicator,
    EditRelatedCompaniesModal,
    AddCompanyAssociationModal,
    PanelEmptyState,
  },
  props: {
    contactId: {
      type: [Number, String],
      required: true,
    },
    // O lapis existe so na pagina de contatos (o pai o desenha no cabecalho da
    // secao, ouvindo `editableChange`). Na sidebar de mais detalhes da
    // conversa ele foi pedido fora: la a lista e consulta, e quem vai mexer no
    // vinculo abre o contato. A acao NAO foi apagada do componente porque este
    // e o unico lugar do app que abre o modal de vinculo -- sem ele nao ha
    // como relacionar contato e empresa pela interface.
    showEditAction: {
      type: Boolean,
      default: true,
    },
  },
  setup() {
    const store = useStore();
    const { isAdmin } = useAdmin();
    const user = computed(() => store.getters.getCurrentUser);
    const accountId = computed(() => store.getters.getCurrentAccountId);
    const userPermissions = computed(() =>
      getUserPermissions(user.value, accountId.value)
    );

    const canManage = computed(
      () =>
        isAdmin.value ||
        hasPermissions(
          ['contact_manage', 'company_manage'],
          userPermissions.value
        )
    );

    // Associar cria a linha pelo endpoint da EMPRESA (`authorize @company,
    // :update?`), entao a permissao e a da tela da empresa, nao a do contato:
    // com so `contact_manage` o botao levaria a um 403.
    const canAssociate = computed(
      () =>
        isAdmin.value ||
        hasPermissions(
          ['company_edit', 'company_manage'],
          userPermissions.value
        )
    );

    return { canManage, canAssociate };
  },
  data() {
    return {
      companies: [],
      isLoading: Boolean(this.contactId),
      showEditModal: false,
      showAssociateModal: false,
      isAssociating: false,
      isSubmitting: false,
    };
  },
  computed: {
    // Com a lista vazia a acao fica no proprio estado vazio; o `+` do
    // cabecalho (do pai) so entra quando ja ha empresas.
    canShowAssociate() {
      return Boolean(
        this.showEditAction && this.canAssociate && !this.isLoading
      );
    },
    canShowAssociateAction() {
      return this.canShowAssociate && this.companies.length > 0;
    },
    // O lapis fica no cabecalho da secao, que e do pai: aqui so se diz se ele
    // deve aparecer.
    canShowEditAction() {
      return Boolean(
        this.showEditAction &&
          this.canManage &&
          !this.isLoading &&
          this.companies.length
      );
    },
  },
  watch: {
    canShowAssociateAction: {
      immediate: true,
      handler(value) {
        this.$emit('associableChange', value);
      },
    },
    canShowEditAction: {
      immediate: true,
      handler(value) {
        this.$emit('editableChange', value);
      },
    },
    contactId: {
      immediate: true,
      handler() {
        this.fetchCompanies();
      },
    },
  },
  methods: {
    async fetchCompanies() {
      if (!this.contactId) {
        this.isLoading = false;
        return;
      }

      this.isLoading = true;
      try {
        const { data } = await ContactAPI.getCompanies(this.contactId);
        this.companies = data.payload || [];
      } catch {
        // Chamado de watcher `immediate`: sem o `catch` a rejeicao ia solta
        // para o console (#1625). A secao cai no estado vazio.
        this.companies = [];
      } finally {
        this.isLoading = false;
      }
    },
    initials(name) {
      return (name || '?')
        .split(' ')
        .map(part => part[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    },
    openCompany(company) {
      this.$router.push({
        name: 'company_profile_dashboard',
        params: {
          accountId: this.$route.params.accountId,
          companyId: company.id,
        },
      });
    },
    openEditModal() {
      this.showEditModal = true;
    },
    openAssociateModal() {
      if (!this.canShowAssociate) return;
      this.showAssociateModal = true;
    },
    closeAssociateModal() {
      this.showAssociateModal = false;
    },
    async submitAssociation({ company_id: companyId, ...params }) {
      this.isAssociating = true;
      try {
        await CompanyAPI.addContact(companyId, {
          ...params,
          contact_id: this.contactId,
        });
        this.showAssociateModal = false;
        await this.fetchCompanies();
        useAlert(
          this.$t('CONTACT_PANEL.SIDEBAR_SECTIONS.RELATE_COMPANY_SUCCESS')
        );
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            error?.response?.data?.message ||
            this.$t('COMPANY.PROFILE.ASSOCIATION.ERROR')
        );
      } finally {
        this.isAssociating = false;
      }
    },
    closeEditModal() {
      this.showEditModal = false;
    },
    async submitRelatedCompanies({ items, validationResults }) {
      this.isSubmitting = true;
      try {
        const currentByCompanyId = new Map();
        this.companies.forEach(c => currentByCompanyId.set(String(c.id), c));

        const desiredCompanyIds = new Set();
        const toCreate = [];

        validationResults.forEach((result, i) => {
          if (result.status === 'found' && result.matches?.length === 1) {
            desiredCompanyIds.add(String(result.matches[0].id));
          } else if (result.status === 'will_create') {
            toCreate.push(items[i]);
          }
        });

        for (const company of this.companies) {
          if (
            !desiredCompanyIds.has(String(company.id)) &&
            company.association_id
          ) {
            await CompanyAPI.removeContact(company.id, company.association_id);
          }
        }

        for (const item of toCreate) {
          const { data } = await CompanyAPI.create(item.fields);
          const newCompany = data.payload || data;
          desiredCompanyIds.add(String(newCompany.id));
        }

        for (const companyId of desiredCompanyIds) {
          if (!currentByCompanyId.has(companyId)) {
            await CompanyAPI.addContact(companyId, {
              contact_id: this.contactId,
            });
          }
        }

        await this.fetchCompanies();
        this.showEditModal = false;
        useAlert(
          this.$t('CONTACT_PANEL.SIDEBAR_SECTIONS.EDIT_RELATED.SUCCESS')
        );
      } catch (error) {
        const message =
          error?.response?.data?.error ||
          this.$t('CONTACT_PANEL.SIDEBAR_SECTIONS.EDIT_RELATED.ERROR');
        useAlert(message);
      } finally {
        this.isSubmitting = false;
      }
    },
  },
};
</script>

<template>
  <!-- Sem recuo lateral: o respiro horizontal e da secao, e os 4px daqui
       deixavam os cartoes mais estreitos que os campos vizinhos da coluna. -->
  <div class="pb-2 space-y-2">
    <div v-if="isLoading">
      <CompanyLoadingIndicator compact />
    </div>

    <template v-else>
      <button
        v-for="company in companies"
        :key="company.association_id || company.id"
        type="button"
        class="flex w-full items-center gap-2 rounded-lg border border-line-faint bg-surface-sunken px-2.5 py-2 text-left transition-colors duration-150 hover:bg-line-faint dark:bg-surface-raised dark:hover:bg-line-faint"
        @click="openCompany(company)"
      >
        <img
          v-if="company.thumbnail"
          :src="company.thumbnail"
          class="w-7 h-7 rounded-md object-cover"
          alt=""
        />
        <span
          v-else
          class="flex w-7 h-7 shrink-0 items-center justify-center rounded-md bg-woot-50 text-xxs font-medium text-woot-700 dark:bg-woot-900/40 dark:text-woot-100"
        >
          {{ initials(company.name) }}
        </span>
        <!-- `text-xs` no nome e `text-xxs` na relacao: e a escala do resto da
             coluna -- os dados do contato, o nome do participante e o titulo
             da secao sao todos `xs`. O cartao usava o tamanho herdado (16px),
             e a empresa acabava gritando mais alto que o contato da conversa.
        -->
        <span class="min-w-0 flex-1">
          <span class="block text-xs font-medium truncate text-body">
            {{ company.name }}
          </span>
          <span class="block truncate text-xxs text-muted">
            {{
              company.relationship_type ||
              company.website_url ||
              $t('COMPANY.PROFILE.NO_RELATIONSHIP')
            }}
          </span>
        </span>
        <fluent-icon icon="chevron-right" size="12" class="text-muted" />
      </button>
    </template>

    <PanelEmptyState
      v-if="!isLoading && !companies.length"
      :message="$t('CONTACT_PANEL.SIDEBAR_SECTIONS.NO_RELATED_COMPANIES')"
      :action-label="
        canShowAssociate
          ? $t('CONTACT_PANEL.SIDEBAR_SECTIONS.RELATE_COMPANY')
          : ''
      "
      @action="openAssociateModal"
    />

    <AddCompanyAssociationModal
      type="contact_company"
      :show="showAssociateModal"
      :existing-companies="companies"
      :is-submitting="isAssociating"
      @close="closeAssociateModal"
      @submit="submitAssociation"
    />

    <EditRelatedCompaniesModal
      :show="showEditModal"
      :contact-id="contactId"
      :current-relationships="companies"
      :is-submitting="isSubmitting"
      @close="closeEditModal"
      @submit="submitRelatedCompanies"
    />
  </div>
</template>
